import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Icon, SegmentedControl } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import KpiTile, { KpiStrip } from "../AgentAnalytics/KpiTile";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { defaultCustom, rangeLabel, resolveRange } from "./range";
import { fetchAnalytics } from "./adTemplateApi";
import { int, money, share } from "./formatters";
import { downloadCsv, toCsv } from "./exportCsv";
import FilterBar, { FormatChips, RangeChips } from "./FilterBar";
import ProjectsView from "./ProjectsView";
import RevenueView from "./RevenueView";
import AdsView from "./AdsView";
import PublicLeadsView from "./PublicLeadsView";
import PublicGeneratedView from "./PublicGeneratedView";
import PublicSignupsView from "./PublicSignupsView";
import PublicFunnel from "./PublicFunnel";
import VideoAdsView from "./VideoAdsView";

const VIEWS = [
  { key: "projects", label: "Projects", icon: "layout-grid" },
  { key: "revenue", label: "Revenue", icon: "dollar-sign" },
  { key: "ads", label: "Live ads", icon: "trending-up" },
  { key: "video", label: "Video ads", icon: "video" },
];

const PUBLIC_VIEWS = [
  { key: "leads", label: "Leads", icon: "users" },
  { key: "generated", label: "Generated ads", icon: "layout-grid" },
  { key: "signups", label: "Signups", icon: "star" },
];

const AREAS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "public", label: "Public page" },
];

const SUBTITLE = {
  dashboard:
    "Each prompt generates several ads, and some of those get launched on Meta. Pick a row to open the project.",
  public:
    "People who used the free Ads Generator page without an account. Pick a row to see their ads.",
};

const FORMAT_VIEWS = ["projects", "revenue", "ads"];

const billedLine = (inr, usd, key) => {
  const parts = [];
  if (inr && inr[key] > 0) parts.push(money(inr[key], "INR"));
  if (usd && usd[key] > 0) parts.push(money(usd[key], "USD"));
  return parts.length ? `${parts.join(" + ")} billed` : "nothing billed";
};

const useStyles = makeStyles({
  root: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    overflowY: "auto",
    padding: "20px 24px 48px",
    background: "#F7F8FA",
  },
  content: {
    maxWidth: 1720,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  title: {
    fontFamily: tokens.fontHeading,
    fontWeight: 700,
    fontSize: 21,
    letterSpacing: "-.3px",
    color: "#232D2F",
  },
  subtitle: {
    fontFamily: tokens.fontBody,
    fontSize: 13,
    color: tokens.textSecondary,
    marginTop: 2,
  },
  refreshBtn: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 10,
    background: tokens.surface,
    color: tokens.primary,
    width: 38,
    height: 38,
    "&:hover": { background: tokens.primaryLight },
  },
  viewChip: {
    background: tokens.surface,
    color: tokens.textSecondary,
    border: `1px solid ${tokens.border}`,
    fontFamily: tokens.fontBody,
    fontSize: 12,
    fontWeight: 600,
  },
  viewChipOn: {
    background: tokens.primaryLight,
    color: tokens.primary,
    border: "1px solid #B9D6D1",
    fontWeight: 700,
    "&:hover, &:focus": { background: tokens.primaryLight },
  },
  exportBtn: {
    borderRadius: 999,
    padding: "6px 14px",
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: "none",
    color: tokens.primary,
    background: tokens.surface,
    border: `1px solid ${tokens.borderStrong}`,
    "&:hover": { background: tokens.primaryLight },
  },
  rangeNote: {
    marginLeft: "auto",
    fontFamily: tokens.fontBody,
    fontSize: 11.5,
    color: tokens.textHint,
  },
  panel: { padding: 16, background: "#FAFBFC" },
});

export default function AdTemplateAnalyticsPage() {
  const classes = useStyles();
  const table = useTableStyles();

  const [area, setArea] = useState("dashboard");
  const [view, setView] = useState("projects");
  const [publicView, setPublicView] = useState("leads");
  const [format, setFormat] = useState("");
  const [preset, setPreset] = useState("7d");
  const [custom, setCustom] = useState(() => defaultCustom());
  const [search, setSearch] = useState("");
  const [band, setBand] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [overview, setOverview] = useState({
    loading: true,
    notFound: false,
    error: null,
    data: null,
  });
  const [exportable, setExportable] = useState({ columns: [], rows: [] });

  const range = resolveRange(preset, custom);

  useEffect(() => {
    let alive = true;
    setOverview((s) => ({ ...s, loading: true }));
    fetchAnalytics("overview", { from: range.from, to: range.to }).then(
      (r) => alive && setOverview({ ...r, loading: false })
    );
    return () => {
      alive = false;
    };
  }, [range.from, range.to, refreshKey]);

  useEffect(() => {
    setExportable({ columns: [], rows: [] });
  }, [area, view, publicView]);

  const onRows = useCallback((rows, columns) => setExportable({ rows, columns }), []);

  const d = overview.data || {};
  const inr = (d.money && d.money.INR) || null;
  const usd = (d.money && d.money.USD) || null;
  const byFormat = d.byFormat || null;
  const imageStats = (byFormat && byFormat.image) || {};
  const videoStats = (byFormat && byFormat.video) || {};
  const videoShare = share(videoStats.adsDelivered, d.adsDelivered);
  const isPublic = area === "public";
  const views = isPublic ? PUBLIC_VIEWS : VIEWS;
  const activeKey = isPublic ? publicView : view;
  const setActiveKey = isPublic ? setPublicView : setView;
  const activeView = views.find((v) => v.key === activeKey) || views[0];
  const showFilters = isPublic || view !== "revenue";
  const showExport = isPublic || view !== "revenue";
  const showFormat = !isPublic && FORMAT_VIEWS.includes(view);
  const viewProps = {
    range,
    search,
    band,
    refreshKey,
    onRows,
    format: showFormat ? format : "",
  };

  const handleExport = () =>
    downloadCsv(
      `ad-templates-${isPublic ? `public-${publicView}` : view}-${range.from}-to-${range.to}.csv`,
      toCsv(exportable.columns, exportable.rows)
    );

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Box className={classes.header}>
          <Box>
            <Typography component="div" className={classes.title}>
              Ad Templates Analytics
            </Typography>
            <Typography component="div" className={classes.subtitle}>
              {SUBTITLE[area]}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              data-testid="refresh-btn"
              className={classes.refreshBtn}
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              <RefreshIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <SegmentedControl
            value={area}
            onChange={setArea}
            options={AREAS}
            data-testid="area-switch"
          />
        </Box>

        {isPublic ? (
          <PublicFunnel
            range={range}
            refreshKey={refreshKey}
            totals={d.public || null}
            totalsLoading={overview.loading}
          />
        ) : (
          <KpiStrip testid="kpi-strip">
            <KpiTile
              icon="layout-grid"
              label="Prompts"
              value={int(d.templates)}
              sub={
                byFormat
                  ? `${int(imageStats.templates)} image · ${int(videoStats.templates)} video`
                  : null
              }
              loading={overview.loading}
              testid="kpi-templates"
            />
            <KpiTile
              icon="list"
              label="Ads generated"
              value={int(d.adsDelivered)}
              sub={d.adsRequested != null ? `of ${int(d.adsRequested)} asked for` : null}
              loading={overview.loading}
              testid="kpi-ads"
            />
            <KpiTile
              icon="image"
              label="Image ads"
              value={int(byFormat ? imageStats.adsDelivered : null)}
              sub={byFormat ? billedLine(inr, usd, "billedImage") : null}
              loading={overview.loading}
              testid="kpi-image"
            />
            <KpiTile
              icon="video"
              label="Video ads"
              value={int(byFormat ? videoStats.adsDelivered : null)}
              sub={
                byFormat
                  ? `${billedLine(inr, usd, "billedVideo")}${
                      videoShare ? ` · ${videoShare} of ads` : ""
                    }`
                  : null
              }
              loading={overview.loading}
              testid="kpi-video"
            />
            <KpiTile
              icon="dollar-sign"
              label="Billed"
              tone="green"
              value={money(inr && inr.billed, "INR")}
              valueColor={inr && inr.billed > 0 ? "#0B8A4B" : undefined}
              sub={
                usd && usd.billed > 0
                  ? `and ${money(usd.billed, "USD")}`
                  : inr
                  ? `${int(inr.chargedAds)} ads charged`
                  : null
              }
              loading={overview.loading}
              testid="kpi-billed"
            />
          </KpiStrip>
        )}

        {showFilters && (
          <FilterBar
            search={search}
            onSearch={setSearch}
            band={band}
            onBand={setBand}
            preset={preset}
            onPreset={setPreset}
            custom={custom}
            onCustom={setCustom}
            showBands={!isPublic && view === "projects"}
            searchPlaceholder={isPublic ? "Name, phone or email" : undefined}
            showSearch={!(isPublic && publicView === "generated")}
            showFormat={showFormat && view !== "revenue"}
            format={format}
            onFormat={setFormat}
          />
        )}

        <Paper elevation={0} className={table.card}>
          <Box className={table.cardHeader}>
            <Typography component="div" className={table.cardTitle}>
              <Icon name={activeView.icon} size={17} style={{ color: tokens.primary }} />
              {activeView.label}
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
              <Box display="flex" gridGap={6}>
                {views.map((v) => (
                  <Chip
                    key={v.key}
                    size="small"
                    clickable
                    label={v.label}
                    data-testid={`view-chip-${v.key}`}
                    onClick={() => setActiveKey(v.key)}
                    className={`${classes.viewChip} ${
                      v.key === activeKey ? classes.viewChipOn : ""
                    }`}
                  />
                ))}
              </Box>
              {showExport && (
                <Tooltip title="Exports the rows currently loaded">
                  <span>
                    <Button
                      data-testid="export-csv"
                      disableElevation
                      disabled={!exportable.rows.length}
                      className={classes.exportBtn}
                      onClick={handleExport}
                    >
                      Export CSV
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          {isPublic && publicView === "leads" && <PublicLeadsView {...viewProps} />}
          {isPublic && publicView === "generated" && <PublicGeneratedView {...viewProps} />}
          {isPublic && publicView === "signups" && <PublicSignupsView {...viewProps} />}
          {!isPublic && view === "projects" && <ProjectsView {...viewProps} />}
          {!isPublic && view === "ads" && <AdsView {...viewProps} />}
          {!isPublic && view === "video" && <VideoAdsView {...viewProps} />}
          {!isPublic && view === "revenue" && (
            <Box className={classes.panel}>
              <Box display="flex" alignItems="center" gridGap={8} mb={1.75}>
                <RangeChips
                  preset={preset}
                  onPreset={setPreset}
                  custom={custom}
                  onCustom={setCustom}
                />
                <Box width={8} />
                <FormatChips format={format} onFormat={setFormat} />
                <span className={classes.rangeNote} data-testid="range-note">
                  {rangeLabel(range)} · IST
                </span>
              </Box>
              <RevenueView range={range} refreshKey={refreshKey} format={format} />
            </Box>
          )}
        </Paper>
      </div>
    </div>
  );
}
